/**
 * Кнопка.
 *
 * Первичная — плотная шалфейная плашка с тёплым внутренним бликом сверху:
 * на бумажном фоне плоская заливка выглядит вырезанной, а блик возвращает ей
 * объём. Нажатие пружинит: интерфейс отвечает раньше, чем ответит сервер.
 */

import clsx from 'clsx'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { haptic } from '@/shared/telegram'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warm'
type Size = 'md' | 'lg' | 'sm'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  variant?: Variant
  size?: Size
  loading?: boolean
  block?: boolean
  icon?: ReactNode
  /** Хаптик на первичных действиях: обратная связь до ответа сервера. */
  hapticKind?: 'tap' | 'success' | 'select' | 'none'
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

// inset-блик сверху и мягкая тень снизу — кнопка лежит на бумаге, а не в ней
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-on-accent active:bg-accent-strong shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_14px_-4px_var(--color-accent)]',
  secondary: 'sticker bg-surface text-text active:bg-surface-muted',
  ghost: 'bg-transparent text-text-muted active:bg-surface-muted',
  danger: 'bg-danger-soft text-danger active:opacity-80',
  warm: 'bg-warm-ink text-on-warm active:opacity-90 shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_4px_14px_-4px_var(--color-warm)]',
}

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-sm rounded-[var(--radius-thumb)]',
  md: 'h-11 px-4 text-body',
  lg: 'h-14 px-5 text-body',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  hapticKind = 'tap',
  className,
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      onClick={(event) => {
        if (hapticKind !== 'none') haptic(hapticKind)
        onClick?.(event)
      }}
      className={clsx(
        'press tap-target inline-flex items-center justify-center gap-2 font-semibold',
        'select-none disabled:opacity-50 disabled:pointer-events-none',
        'rounded-[var(--radius-button)] tracking-[-0.01em]',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  )
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  )
}
